import { Divider, Grid } from '@material-ui/core'
import React, { useContext, useEffect } from 'react'
import { SectionRadioButton, SectionSelect, SectionSpecialSelect } from '../Core/Form/FormComponents'
import { statesWithoutDiminishingDeductible } from '../../Constants'
import { SessionContextStore } from '../../context/SessionContext'

export const MotorcycleCoveragesForm = ({
  motorcycleId,
  options,
  values,
  onChange,
  errors,
  isDiminishingDeductible,
  onDiminishingDeductibleChange,
  isNewFlow,
  needTooltip = true,
  isPolicyManagment = false,
  shouldHaveDivider = true,
  // the following fields were added as customizations for the new design
  popover,
}) => {
  const sessionContext = useContext(SessionContextStore)
  const hasDiminishingDeductible = !statesWithoutDiminishingDeductible.includes(sessionContext.user.administrativeDivision)

  const hasComprehensive = !(options.physical_damage_comprehensive.length > 0
    ? options.physical_damage_comprehensive.filter(option => option.label === 'Not Included')[0].value ===
      values.physical_damage_comprehensive
    : options.physical_damage_other_then_collision.filter(option => option.label === 'Not Included')[0].value ===
      values.physical_damage_other_then_collision)

  const handleDiminishingDeductibleChange = (name, value) => {
    onDiminishingDeductibleChange(motorcycleId, value)
  }

  const onFieldChange = (name, value) => {
    onChange && onChange(motorcycleId, name, value)
  }

  const getCoverageTooltip = coverageName => {
    let value = options[coverageName]
    return value && value.length > 1 ? value[0].tooltip : undefined
  }

  useEffect(() => {
    if (!hasComprehensive) handleDiminishingDeductibleChange(null, false)
  }, [hasComprehensive])

  return (
    <Grid item xs={12} md={9}>
      {values.physical_damage_comprehensive ? (
        <SectionSelect
          onChange={onFieldChange}
          name="physical_damage_comprehensive"
          title="Comprehensive"
          GTMname="Comp-limit"
          errors={errors}
          value={values.physical_damage_comprehensive}
          buttons={options.physical_damage_comprehensive}
          tooltip={needTooltip && getCoverageTooltip('physical_damage_comprehensive')}
          {...(needTooltip && popover && { Popover: () => popover(getCoverageTooltip('physical_damage_comprehensive')) })}
          subtitle="Pays for damage to your vehicle from events out of your control."
          removeDeductible
          shouldHaveDivider={shouldHaveDivider}
          isPolicyManagment
        />
      ) : (
        <SectionSelect
          onChange={onFieldChange}
          name="physical_damage_other_then_collision"
          title="Other Than Collision"
          GTMname="Other-Then-Collision-limit"
          errors={errors}
          value={values.physical_damage_other_then_collision}
          buttons={options.physical_damage_other_then_collision}
          tooltip={needTooltip && getCoverageTooltip('physical_damage_other_then_collision')}
          {...(needTooltip && popover && { Popover: () => popover(getCoverageTooltip('physical_damage_other_then_collision')) })}
          subtitle="Pays for damage to your vehicle from events out of your control."
          removeDeductible
          shouldHaveDivider={shouldHaveDivider}
          isPolicyManagment
        />
      )}

      <SectionSelect
        onChange={onFieldChange}
        title="Collision"
        GTMname="Coll-limit"
        errors={errors}
        name="physical_damage_collision"
        value={values.physical_damage_collision}
        buttons={options.physical_damage_collision}
        tooltip={needTooltip && getCoverageTooltip('physical_damage_collision')}
        {...(needTooltip && popover && { Popover: () => popover(getCoverageTooltip('physical_damage_collision')) })}
        subtitle="Pays for damage to your vehicle if you’re in an accident, no matter who is at fault."
        shouldHaveDivider={
          shouldHaveDivider &&
          ((options.uninsured_motorist_property_damage && options.uninsured_motorist_property_damage.length > 0) ||
            hasComprehensive ||
            (options.pedestrian_first_party_benefits && options.pedestrian_first_party_benefits.length > 0) ||
            (options.enhanced_underinsured_motorist_property_damage && options.enhanced_underinsured_motorist_property_damage.length > 0) ||
            (options.roadside_assistance && options.roadside_assistance.length > 0))
        }
        removeDeductible
        isPolicyManagment
      />
      {hasComprehensive && hasDiminishingDeductible && (
        <SectionSelect
          onChange={handleDiminishingDeductibleChange}
          title="Diminishing Deductible"
          GTMname="Diminishing-deductible-limit"
          value={isDiminishingDeductible}
          buttons={[
            { label: 'Not Included', value: false },
            { label: 'Included', value: true },
          ]}
          tooltip={
            needTooltip && "This coverage takes 25% off your deductible(s) each year you don't have a claim, all the way down to $0."
          }
          {...(needTooltip &&
            popover && {
              Popover: () =>
                popover(
                  getCoverageTooltip(
                    "This coverage takes 25% off your deductible(s) each year you don't have a claim, all the way down to $0."
                  )
                ),
            })}
          shouldHaveDivider={
            shouldHaveDivider &&
            ((options.uninsured_motorist_property_damage && options.uninsured_motorist_property_damage.length > 0) ||
              (options.pedestrian_first_party_benefits && options.pedestrian_first_party_benefits.length > 0) ||
              (options.enhanced_underinsured_motorist_property_damage &&
                options.enhanced_underinsured_motorist_property_damage.length > 0) ||
              (options.roadside_assistance && options.roadside_assistance.length > 0))
          }
          isPolicyManagment
        />
      )}

      {options.uninsured_motorist_property_damage &&
        options.uninsured_motorist_property_damage.some(x => x.type === 'vehicle') &&
        options.uninsured_motorist_property_damage.length > 1 && (
          <SectionSelect
            onChange={onFieldChange}
            name="uninsured_motorist_property_damage"
            title="UM Property Damage"
            GTMname="UM-motorist-limit"
            errors={errors}
            value={values.uninsured_motorist_property_damage}
            buttons={options.uninsured_motorist_property_damage}
            tooltip={needTooltip && getCoverageTooltip('uninsured_motorist_property_damage')}
            {...(needTooltip && popover && { Popover: () => popover(getCoverageTooltip('uninsured_motorist_property_damage')) })}
            subtitle="Helps you to pay for vehicle/personal property damage in case of an accident with uninsured guilty motorist."
            shouldHaveDivider={options.roadside_assistance && options.roadside_assistance.length > 0}
            isPolicyManagment
          />
        )}
      {options.income_loss_benefits && options.income_loss_benefits.some(x => x.type === 'vehicle') && (
        <SectionSelect
          onChange={onFieldChange}
          name="income_loss_benefits"
          title="income_loss_benefits"
          GTMname="UM-motorist-limit"
          errors={errors}
          value={values.income_loss_benefits}
          buttons={options.income_loss_benefits}
          tooltip={needTooltip && getCoverageTooltip('income_loss_benefits')}
          {...(needTooltip && popover && { Popover: () => popover(getCoverageTooltip('income_loss_benefits')) })}
          subtitle="Helps you to pay for vehicle/personal property damage in case of an accident with uninsured guilty motorist."
          shouldHaveDivider={options.roadside_assistance && options.roadside_assistance.length > 0}
          isPolicyManagment
        />
      )}

      {options.enhanced_underinsured_motorist_property_damage &&
        options.enhanced_underinsured_motorist_property_damage.length > 0 &&
        options.enhanced_underinsured_motorist_property_damage.some(x => x.type === 'vehicle') && (
          <SectionSpecialSelect
            onChange={onFieldChange}
            name="enhanced_underinsured_motorist_property_damage"
            title="Enhanced UIM Property Damage"
            GTMname="Enhanced-UIM-limit"
            errors={errors}
            value={values.enhanced_underinsured_motorist_property_damage}
            buttons={options.enhanced_underinsured_motorist_property_damage}
            tooltip={needTooltip && getCoverageTooltip('enhanced_underinsured_motorist_property_damage')}
            subtitle="Helps you to pay for vehicle/personal property damage in case of an accident with uninsured guilty motorist."
            shouldHaveDivider={false}
            isPolicyManagment
          />
        )}
      {options.pedestrian_first_party_benefits &&
        options.pedestrian_first_party_benefits.length > 0 &&
        options.pedestrian_first_party_benefits.some(x => x.type === 'vehicle') && (
          <SectionSpecialSelect
            onChange={onFieldChange}
            name="pedestrian_first_party_benefits"
            title="Pedestrian First Party Benefits"
            GTMname="Pedestrian-first-party-benefits"
            errors={errors}
            value={values.pedestrian_first_party_benefits}
            buttons={options.pedestrian_first_party_benefits}
            tooltip={needTooltip && getCoverageTooltip('pedestrian_first_party_benefits')}
            shouldHaveDivider={false}
            isPolicyManagment
          />
        )}

      {options.rental_reimbursement &&
        options.rental_reimbursement.length > 0 &&
        options.rental_reimbursement.some(x => x.type === 'vehicle') && (
          <SectionSelect
            name="rental_reimbursement"
            onChange={onFieldChange}
            GTMname="Rent-limit"
            errors={errors}
            value={values.rental_reimbursement}
            buttons={options.rental_reimbursement}
            tooltip={getCoverageTooltip('rental_reimbursement')}
            title="Transportation Expenses"
            subtitle="Helps you to pay for vehicle/personal property damage in case of an accident with uninsured guilty motorist."
            isPolicyManagment
            shouldHaveDivider={false}
          />
        )}
    </Grid>
  )
}
